/**
 * A tank entity — the player's, or an enemy's. Wraps the assembled prefab and
 * owns the gameplay state (position, body heading, turret heading, health). AI
 * lives in systems/enemies.ts; player control lives in main.ts. This class just
 * holds state and pushes it to the meshes each frame.
 *
 * Enemy tanks can wear a mutual's avatar on the body (planar top-down UV) and
 * carry a billboard name label, mirroring the zombies "mutuals as enemies" skin.
 */

import * as THREE from "three";
import { buildTank, buildMecha, MUZZLE_LOCAL, type BuiltTank, type BuiltMecha, type MechaLeg } from "../tank/prefab";
import { type TankType, MECHA } from "../config";
import type { Arena } from "../world/arena";
import { sfx } from "../engine/sfx";

// mecha walker: procedural footstep IK. Feet PLANT on the ground and only step when they
// stray; the body height is driven separately (crouch) and the 2-bone IK folds the knees to
// keep the feet planted — so crouching lowers the turret to the floor without clipping.
const MECHA_CROUCH_DROP = 38; // body lowers this far when fully crouched (knees fold via IK)
const MECHA_STEP_DIST = 11;   // a foot this far from its rest spot takes a step (small → stays planted, snaps often)
const MECHA_STEP_FORCE = 22;  // strayed THIS far → step NOW regardless of gait (anti-starvation: never let a foot drag/stretch)
const MECHA_STEP_TIME = 0.05; // seconds per step — NEAR-INSTANT placement (decoupled from move speed); a quick snap, not a slow glide
const MECHA_STEP_LEAD = 0.3;  // step LANDS this many seconds of travel ahead, so it plants where the body is going
const MECHA_STEP_HEIGHT = 14; // foot lift-arc apex during a step
const MECHA_DODGE_TIME = 0.12; // a leg DODGE step is snappier (yank the foot from the bullet)
const MECHA_DODGE_LIFT = 6;    // dodge steps stay low (a quick slide aside)
// IK scratch (module-shared; never retained across calls)
const _mv = new THREE.Vector3(), _mn = new THREE.Vector3(), _mb = new THREE.Vector3();
const _mpole = new THREE.Vector3(), _mfoot = new THREE.Vector3(), _mknee = new THREE.Vector3(), _mrest = new THREE.Vector3();
const _mdir = new THREE.Vector3();
const _muzW = new THREE.Vector3(); // scratch: mecha turret-pivot world position for muzzle height
// bone-pose scratch: rotation × along-axis stretch baked into a manual matrix
const _bq = new THREE.Quaternion();
const _bR = new THREE.Matrix4(), _bS = new THREE.Matrix4(), _bM = new THREE.Matrix4();
// triad-pose scratch: R = M_pose · M_restᵀ locks bone ROLL to the bend plane (asymmetric leg meshes)
const _triRest = new THREE.Matrix4(), _triPose = new THREE.Matrix4();
const _tSide = new THREE.Vector3(), _tNorm = new THREE.Vector3(), _tAxis = new THREE.Vector3();
const _pSide = new THREE.Vector3(), _pNorm = new THREE.Vector3(), _poseNormal = new THREE.Vector3();

// flat unit square (lying on the XZ plane) for the spawn telegraph: a tank rising out
// of the ground shows a red square (blue for allies) where it will surface.
const riseMarkerGeom = new THREE.PlaneGeometry(1, 1);
riseMarkerGeom.rotateX(-Math.PI / 2);

/** Convert a target-relative direction into a body/turret yaw (0 yaw = -Z). */
export function yawToward(dx: number, dz: number): number {
  return Math.atan2(-dx, -dz);
}

/** Deterministic vivid colour from a string (a mutual's name → a hue). */
export function colorFromString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = (h >>> 0) % 360;
  const sat = 0.6, light = 0.55;
  const a = sat * Math.min(light, 1 - light);
  const f = (n: number) => {
    const k = (n + hue / 30) % 12;
    const c = light - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * c);
  };
  return (f(0) << 16) | (f(8) << 8) | f(4);
}

/** Smoothly rotate `from` toward `to` (shortest way) at rate*dt, clamped. */
export function approachAngle(from: number, to: number, maxStep: number): number {
  let d = to - from;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  if (Math.abs(d) <= maxStep) return to;
  return from + Math.sign(d) * maxStep;
}

let UID = 0;

export class Tank {
  readonly uid = ++UID;
  parts: BuiltTank;
  pos = new THREE.Vector3();
  bodyYaw = 0;
  /** WORLD yaw of the turret (not relative to the body) */
  turretYaw = 0;
  alive = true;
  readonly isPlayer: boolean;
  readonly type: TankType | null;
  name: string;
  health: number;
  floorY = 0;
  /** which vertical level the tank is on (0 = ground … road levels above) */
  level = 0;
  /** combat team: 0 = player + allies (friendly), 1 = enemies. A shell only damages
   *  tanks on the OTHER team — no friendly fire between the player and a mercenary. */
  team = 0;
  /** transient: the tank that landed the lethal hit (read once by the kill feed at cull). */
  killer?: Tank;

  // last position where this tank dropped a tread mark (distance-gated)
  treadX = 0;
  treadZ = 0;

  // sfx motion scratch — prev XZ + prev turret yaw, sampled once/frame by the audio scan
  // (no velocity is exposed elsewhere). sfxInit guards a fresh tank's first-frame phantom delta.
  sfxPrevX = 0;
  sfxPrevZ = 0;
  sfxPrevTurret = 0;
  sfxInit = false;

  // lift ride (riding a small intra-floor elevator between levels)
  riding = false;
  private rideT = 0;
  private rideFromY = 0;
  private rideToY = 0;
  private rideToLevel = 0;
  private rideCx = 0;
  private rideCz = 0;

  // being thrown by a Thrower — arcing through the air to a new spot (AI frozen)
  thrown = false;
  private throwT = 0;
  private throwFromX = 0;
  private throwFromZ = 0;
  private throwToX = 0;
  private throwToZ = 0;
  private throwBaseY = 0;

  // enemy AI scratch (unused for the player)
  fireTimer = 0;
  thinkTimer = 0;
  wanderYaw = 0;
  wanderTimer = 0;
  riseTime = 0; // >0 while emerging from the spawn platform
  mineTimer = 0;
  stuckT = 0; // mercenary nav: time spent wedged against geometry (drives the sidestep juke)
  // sticky wall-follow (forward-only nav): when pinned on an angled face (esp. a wedge
  // diagonal), glide along the committed contact tangent until clear instead of jittering.
  blockNX = 0; blockNZ = 0; // unit contact normal from the last resolve push-out
  blockT = 0; // >0 while a wall-follow is active (decays once clear)
  blockSide = 1; // committed way around the obstacle (+1/−1), chosen on first contact
  laserStun = 0; // laser enemy: >0 while its beam is staggered (shot by a player shell)
  laserDmgT = 0; // laser enemy: countdown to the next beam damage tick
  laserPhase: "charge" | "beam" = "beam"; // laser cycle: telegraph (charge) → live (beam). Starts "beam"+timer 0 so the FIRST update flips straight into a charge (a fresh laser winds up before it fires).
  laserPhaseTimer = 0; // countdown within the current laser phase
  laserFxT = 0; // throttle for the laser emitter muzzle-glow (so the continuous beam doesn't spawn 60 flashes/s)
  invulnT = 0; // >0 while an Aegis is shielding this tank — all damage is blocked (refreshed each frame by the Aegis)
  burstLeft = 0; // MLRS: arc shells remaining in the current salvo
  cardLevel = 0; // CARD MODE: the deploy LEVEL of this ally's card (0 for non-card tanks); scales Hatchery minions
  // god-aim sniper: incrementally-scanned multi-bounce bank solution. aimScan is
  // the next candidate index to test (-1 = idle/locked); the search is spread
  // across frames under a global budget so it never spikes a frame.
  aimTimer = 0;
  aimScan = -1;
  aimDirX = 0;
  aimDirZ = 0;
  aimValid = false;

  private label?: THREE.Sprite;
  private labelCanvas?: HTMLCanvasElement;
  private labelTex?: THREE.CanvasTexture;
  private isMutual = false;
  private _forward = new THREE.Vector3();

  // armour shields shown above tougher enemies (health-1 absorbable hits)
  private shieldSprite?: THREE.Sprite;
  private shieldCanvas?: HTMLCanvasElement;
  private shieldTex?: THREE.CanvasTexture;
  shieldFlash = 0; // >0 while the shields briefly flash after absorbing a hit
  private riseMarker?: THREE.Mesh; // ground spawn telegraph shown while emerging (riseTime>0)
  private homeScene?: THREE.Scene; // scene this tank lives in (for self-managing the rise marker)

  /** muzzle point(s) in the turret-pivot frame; multi-barrel turrets (TOW) cycle them */
  muzzleLocals: THREE.Vector3[] = [MUZZLE_LOCAL];
  private muzzleCycle = 0;
  private spinBarrel?: THREE.Object3D; // gatling rotating barrel cluster
  private spin = 0; // 0..1 spin-up ramp (gatling)

  // ---- MECHA walker state (only when type.mecha; inert otherwise) ----
  isMecha = false;
  private mecha?: BuiltMecha;
  mechaPhase: "roam" | "crouch" | "fire" | "rise" | "flee" = "roam";
  mechaCrouch = 0;   // 0 = striding (raised, legs-only target), 1 = fully crouched (base = weak point)
  walkPhase = 0;     // leg-cycle accumulator, driven by distance walked
  mechaDwell = 0;    // seconds left in the crouched firing window
  mechaFleeT = 0;    // seconds left fleeing after a shot threatened the base
  mechaRaise = 0;    // 0..1 extend-UP progress to shoot a tank on a road
  mechaRaiseY = 0;   // world height the body raises to (the road's height), when engaging up
  private mechaLastX = 0; private mechaLastZ = 0; // last body pos, for the footstep lead/velocity
  mechaWanderX = 0; mechaWanderZ = 0; // current free-roam target (over walls, in-bounds)
  legHp: number[] = [];
  legAlive: boolean[] = [];
  legWorld: { x: number; z: number; alive: boolean }[] = []; // world XZ of each foot (raised hit targets)
  legShield = 0;     // MECHA leg shield charges — leg hits DRAIN these before any leg can pop
  toppling = false;
  toppleT = 0;       // 0..1 topple-fall progress (explodes at 1)
  private mechaSeeded = false; // planted feet initialised at the spawn pose
  /** true after losing the 1st leg → moves slower */
  get limp(): boolean { return this.isMecha && this.legsLost >= 1; }
  /** crouched enough that the base is the weak point (and it can fire) */
  get crouched(): boolean { return this.isMecha && this.mechaCrouch > 0.5; }
  get legsLost(): number { let n = 0; for (const a of this.legAlive) if (!a) n++; return n; }

  constructor(geo: Parameters<typeof buildTank>[0], type: TankType | null, name: string, isPlayer: boolean, layerIdx = 0) {
    this.type = type;
    this.isPlayer = isPlayer;
    this.team = isPlayer ? 0 : 1; // enemies default to team 1; an ally is flipped back to 0 after construction
    this.name = name;
    this.health = type?.health ?? 1;
    const color = isPlayer ? 0x2f6fe0 : type!.color;
    // MECHA modifier: a tall 4-legged walker wearing this type's turret (any type can wear it).
    if (type?.mecha) {
      const built = buildMecha(geo, color, type.id, layerIdx);
      built.root.scale.setScalar(MECHA.scale);
      this.parts = built;
      this.mecha = built;
      this.isMecha = true;
      this.legShield = MECHA.shields; // a consistent leg-shield buffer (base `health` stays the type's own, for the crouch weak point)
      for (let i = 0; i < 4; i++) { this.legHp[i] = MECHA.legHp; this.legAlive[i] = true; this.legWorld[i] = { x: 0, z: 0, alive: true }; }
    } else {
      // per-type turret + body style (and ghost invisibility) are resolved inside buildTank
      this.parts = buildTank(geo, color, !!type?.ghost, type?.id, layerIdx);
    }
    this.muzzleLocals = this.parts.muzzles;
    this.spinBarrel = this.parts.spinBarrel;
    // turretless types (mobile mine + the support tanks that never fire)
    if (type?.kamikaze || type?.spawner || type?.thrower) this.parts.turretPivot.visible = false;
    // ghosts carry no name label (it would give their position away)
    if (!isPlayer && !type?.ghost) this.buildLabel();
    // build the shield row for every non-ghost enemy (hidden until health>1) so a
    // Shield Generator can light up shields on a tank that started with none
    if (!isPlayer && !type?.ghost) this.buildShields();
  }

  get root(): THREE.Group {
    return this.parts.root;
  }

  /** The tank's body colour (for death-gib debris). */
  bodyColorHex(): number {
    return this.parts.bodyMat.color.getHex();
  }

  /** Tag this enemy with a mutual's name (shown on its label). The body colour
   *  stays the TANK TYPE's colour — identity is conveyed by the type, not the hue. */
  setMutual(name: string): void {
    this.name = name;
    this.isMutual = true;
    this.redrawLabel();
  }

  private buildLabel(): void {
    this.labelCanvas = document.createElement("canvas");
    this.labelCanvas.width = 384;
    this.labelCanvas.height = 72;
    this.labelTex = new THREE.CanvasTexture(this.labelCanvas);
    const mat = new THREE.SpriteMaterial({ map: this.labelTex, transparent: true, depthTest: true });
    this.label = new THREE.Sprite(mat);
    this.label.scale.set(34, 6.4, 1);
    this.redrawLabel();
  }

  private redrawLabel(): void {
    if (!this.labelCanvas || !this.labelTex) return;
    const c = this.labelCanvas.getContext("2d")!;
    const W = this.labelCanvas.width;
    c.clearRect(0, 0, W, this.labelCanvas.height);
    // mutual tanks show "name [Type]"; plain enemies just show the type once
    const label = this.isMutual && this.type ? `${this.name} [${this.type.name}]` : this.name;
    c.textAlign = "center";
    c.textBaseline = "middle";
    let fontPx = 34;
    c.font = `bold ${fontPx}px 'Courier New', monospace`;
    const maxW = W - 24;
    const w = c.measureText(label).width;
    if (w > maxW) fontPx = Math.max(12, Math.floor((fontPx * maxW) / w));
    c.font = `bold ${fontPx}px 'Courier New', monospace`;
    c.lineWidth = 5;
    c.strokeStyle = "rgba(0,0,0,0.85)";
    c.fillStyle = "#ffffff";
    c.strokeText(label, W / 2, 36);
    c.fillText(label, W / 2, 36);
    this.labelTex.needsUpdate = true;
  }

  private buildShields(): void {
    this.shieldCanvas = document.createElement("canvas");
    this.shieldCanvas.width = 220;
    this.shieldCanvas.height = 48;
    this.shieldTex = new THREE.CanvasTexture(this.shieldCanvas);
    const mat = new THREE.SpriteMaterial({ map: this.shieldTex, transparent: true, depthTest: true });
    this.shieldSprite = new THREE.Sprite(mat);
    this.shieldSprite.scale.set(18, 3.9, 1);
    this.drawShields();
  }

  /** Draw one shield badge (rounded top, pointed bottom) at (cx,cy). */
  private shieldBadge(c: CanvasRenderingContext2D, cx: number, cy: number, r: number, bright: boolean): void {
    c.beginPath();
    c.moveTo(cx, cy - r);
    c.lineTo(cx + r * 0.82, cy - r * 0.5);
    c.lineTo(cx + r * 0.82, cy + r * 0.25);
    c.lineTo(cx, cy + r);
    c.lineTo(cx - r * 0.82, cy + r * 0.25);
    c.lineTo(cx - r * 0.82, cy - r * 0.5);
    c.closePath();
    c.fillStyle = bright ? "#e6f3ff" : "#5dade2";
    c.fill();
    c.lineWidth = 3; c.strokeStyle = "rgba(8,38,66,0.9)"; c.stroke();
  }

  /** Redraw the shield row to reflect remaining armour (health-1 shields). */
  private drawShields(): void {
    if (!this.shieldCanvas || !this.shieldTex || !this.shieldSprite) return;
    const n = Math.max(0, this.health - 1);
    const c = this.shieldCanvas.getContext("2d")!;
    const W = this.shieldCanvas.width, H = this.shieldCanvas.height;
    c.clearRect(0, 0, W, H);
    this.shieldSprite.visible = n > 0;
    if (n === 0) { this.shieldTex.needsUpdate = true; return; }
    const r = 18, gap = 10, step = r * 1.64 + gap, total = n * r * 1.64 + (n - 1) * gap;
    let x = (W - total) / 2 + r * 0.82;
    for (let i = 0; i < n; i++) { this.shieldBadge(c, x, H / 2, r, this.shieldFlash > 0); x += step; }
    this.shieldTex.needsUpdate = true;
  }

  /** Called after a non-lethal hit decrements health: flash + redraw the shields. */
  absorbHit(): void {
    this.shieldFlash = 0.22;
    this.drawShields();
  }

  /** A Shield Generator grants this ally another shield, up to a cap. Returns true
   *  if a shield was actually added (so the caller can play the aura). */
  grantShield(cap = 4): boolean {
    if (this.health >= cap) return false;
    this.health += 1;
    this.shieldFlash = 0.3;
    this.drawShields();
    return true;
  }

  /** Tick the shield flash; redraw once when it ends. Returns nothing. */
  tickShield(dt: number): void {
    if (this.shieldFlash > 0) {
      this.shieldFlash -= dt;
      if (this.shieldFlash <= 0) this.drawShields();
    }
  }

  addToScene(scene: THREE.Scene): void {
    this.homeScene = scene;
    scene.add(this.root);
    if (this.label) scene.add(this.label);
    if (this.shieldSprite) scene.add(this.shieldSprite);
    // a tank emerging from the ground telegraphs its spawn with a ground square
    // (red = enemy, blue = ally) — like the artillery indicator, though it deals no damage
    if (this.riseTime > 0 && !this.riseMarker) {
      const color = this.team === 0 ? 0x3fa9f5 : 0xff2a18;
      this.riseMarker = new THREE.Mesh(riseMarkerGeom, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.45, depthWrite: false, depthTest: false, side: THREE.DoubleSide }));
      this.riseMarker.renderOrder = 4;
      this.riseMarker.position.set(this.pos.x, this.floorY + 0.6, this.pos.z);
      this.riseMarker.scale.setScalar(30);
      scene.add(this.riseMarker);
    }
  }

  /** Drop the spawn telegraph (once the tank has fully surfaced, or on removal). */
  private clearRiseMarker(): void {
    if (!this.riseMarker) return;
    this.homeScene?.remove(this.riseMarker);
    (this.riseMarker.material as THREE.Material).dispose();
    this.riseMarker = undefined;
  }

  removeFromScene(scene: THREE.Scene): void {
    scene.remove(this.root);
    if (this.label) scene.remove(this.label);
    if (this.shieldSprite) scene.remove(this.shieldSprite);
    this.clearRiseMarker();
  }

  /** Unit forward vector of the turret (where shells fly). */
  forward(out = this._forward): THREE.Vector3 {
    return out.set(-Math.sin(this.turretYaw), 0, -Math.cos(this.turretYaw));
  }

  /** World-space muzzle point for the CURRENT barrel. Pure read (no side effects) — it's
   *  also called every frame for the aim line + laser sights, so it must not cycle. */
  muzzle(out: THREE.Vector3): THREE.Vector3 {
    const m = this.muzzleLocals[this.muzzleCycle % this.muzzleLocals.length] ?? MUZZLE_LOCAL;
    const fx = -Math.sin(this.turretYaw), fz = -Math.cos(this.turretYaw); // turret forward
    const rx = -fz, rz = fx; // turret right (lateral barrel offset)
    // XZ is the body's (turret lift is vertical-only). For a MECHA the turret rides the bodyGroup
    // (crouch/raise) on a tall 44-stud mount, so Y must come from the pivot's REAL world height —
    // else a TOW missile / shell spawns down at the ground instead of the raised launcher.
    let y = this.floorY + m.y;
    if (this.isMecha) {
      this.parts.turretPivot.getWorldPosition(_muzW); // includes floorY + bodyGroup lift + mount, scaled
      y = _muzW.y + m.y * MECHA.scale;
    }
    out.set(
      this.pos.x + fx * -m.z + rx * m.x,
      y,
      this.pos.z + fz * -m.z + rz * m.x,
    );
    return out;
  }

  /** Step to the next barrel — call AFTER a shot so multi-barrel turrets (TOW) alternate. */
  advanceBarrel(): void {
    if (this.muzzleLocals.length > 1) this.muzzleCycle++;
  }

  /** Spin the gatling's barrel cluster: ramps up while firing, coasts down otherwise. */
  updateSpin(dt: number, firing: boolean): void {
    if (!this.spinBarrel) return;
    this.spin += ((firing ? 1 : 0) - this.spin) * Math.min(1, dt * 5);
    this.spinBarrel.rotation.z += this.spin * 22 * dt;
  }

  /** Begin riding a lift from the tank's current level to `toLevel`, between
   *  world heights `fromY`→`toY`, locked over the lift cell at (cx,cz). */
  startRide(toLevel: number, fromY: number, toY: number, cx: number, cz: number): void {
    this.riding = true;
    this.rideT = 0;
    this.rideFromY = fromY;
    this.rideToY = toY;
    this.rideToLevel = toLevel;
    this.rideCx = cx;
    this.rideCz = cz;
  }

  /** Advance a lift ride (~RIDE_DUR seconds). Eases the tank up/down the shaft
   *  while gently snapping it to the lift-cell center. Returns true when done. */
  advanceRide(dt: number): boolean {
    const RIDE_DUR = 0.9;
    this.rideT = Math.min(1, this.rideT + dt / RIDE_DUR);
    const k = Math.min(1, 9 * dt);
    this.pos.x += (this.rideCx - this.pos.x) * k;
    this.pos.z += (this.rideCz - this.pos.z) * k;
    const e = this.rideT < 0.5 ? 2 * this.rideT * this.rideT : 1 - Math.pow(-2 * this.rideT + 2, 2) / 2;
    this.floorY = this.rideFromY + (this.rideToY - this.rideFromY) * e;
    this.sync();
    if (this.rideT >= 1) {
      this.riding = false;
      this.level = this.rideToLevel;
      this.pos.x = this.rideCx; this.pos.z = this.rideCz;
      this.floorY = this.rideToY;
      this.sync();
      return true;
    }
    return false;
  }

  /** Begin being thrown (no damage) onto the ground at (cx,cz), base height baseY. */
  throwTo(cx: number, cz: number, baseY: number): void {
    this.thrown = true;
    this.riding = false; this.rideT = 0; // cancel any lift ride so it doesn't resume + teleport back up
    this.throwT = 0;
    this.throwFromX = this.pos.x; this.throwFromZ = this.pos.z;
    this.throwToX = cx; this.throwToZ = cz;
    this.throwBaseY = baseY;
    this.level = 0; // lands on the ground near the player
  }

  /** Advance a throw arc: lerp XZ + a vertical hop, tumbling. Resumes AI on landing. */
  advanceThrow(dt: number): boolean {
    const THROW_DUR = 0.55;
    this.throwT = Math.min(1, this.throwT + dt / THROW_DUR);
    const t = this.throwT;
    this.pos.x = this.throwFromX + (this.throwToX - this.throwFromX) * t;
    this.pos.z = this.throwFromZ + (this.throwToZ - this.throwFromZ) * t;
    this.floorY = this.throwBaseY + Math.sin(t * Math.PI) * 64; // arc up and back down
    this.bodyYaw += dt * 14; // tumble while airborne
    this.sync();
    if (this.throwT >= 1) {
      this.thrown = false;
      this.pos.x = this.throwToX; this.pos.z = this.throwToZ;
      this.floorY = this.throwBaseY;
      this.treadX = this.pos.x; this.treadZ = this.pos.z; // don't streak a tread across the flight
      this.sync();
      return true;
    }
    return false;
  }

  /** Visual for emerging from the spawn platform: sit `below` studs under the
   *  floor, body facing its heading, turret centered, label held above. */
  setRiseVisual(below: number): void {
    this.root.position.set(this.pos.x, this.floorY - below, this.pos.z);
    this.root.rotation.y = this.bodyYaw;
    this.parts.turretPivot.rotation.y = 0;
    if (this.label) this.label.position.set(this.pos.x, this.floorY + 20, this.pos.z);
    if (this.shieldSprite) this.shieldSprite.position.set(this.pos.x, this.floorY + 28, this.pos.z);
  }

  /** MECHA: a foot's NEUTRAL world position (rest stance under the body), resting on the
   *  surface (the top of a maze box if one is under it, else the ground). */
  private restFootWorld(leg: MechaLeg, out: THREE.Vector3, arena: Arena | null): void {
    const c = Math.cos(this.bodyYaw), s = Math.sin(this.bodyYaw), k = MECHA.scale;
    const fx = leg.restFoot.x * k, fz = leg.restFoot.z * k;
    const x = this.pos.x + (fx * c + fz * s), z = this.pos.z + (-fx * s + fz * c);
    out.set(x, this.floorY + (arena ? arena.surfaceTop(x, z) : 0), z);
  }

  /** MECHA: footstep controller — plant feet on the surface, step a foot when it strays, in a
   *  diagonal gait, LEADING the step ahead by the body's velocity so the legs keep up at speed.
   *  Feet rest on box tops. Updates each foot's WORLD position + the hit-target legWorld. */
  updateMechaLegs(dt: number, arena: Arena | null): void {
    if (!this.mecha) return;
    const legs = this.mecha.legs;
    if (!this.mechaSeeded) { for (const leg of legs) this.restFootWorld(leg, leg.planted, arena); this.mechaSeeded = true; this.mechaLastX = this.pos.x; this.mechaLastZ = this.pos.z; }
    // body velocity → a forward lead so the foot plants where the body is heading (no gliding)
    const bvx = this.pos.x - this.mechaLastX, bvz = this.pos.z - this.mechaLastZ;
    this.mechaLastX = this.pos.x; this.mechaLastZ = this.pos.z;
    const moveLen = Math.hypot(bvx, bvz);
    // lead the step ahead by the body's travel — but CAP it below STEP_DIST so a freshly-planted foot
    // rests UNDER the step threshold (else it instantly re-qualifies, one pair hogs every step slot and
    // the opposite pair starves + drags + stretches — the old "gliding" bug).
    const lead = Math.min((moveLen / Math.max(dt, 1e-4)) * MECHA_STEP_LEAD, MECHA_STEP_DIST * 0.6);
    const ldx = moveLen > 1e-4 ? bvx / moveLen : 0, ldz = moveLen > 1e-4 ? bvz / moveLen : 0;
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i];
      if (!this.legAlive[i]) { this.legWorld[i].alive = false; continue; } // destroyed leg: no stepping
      if (leg.stepping) {
        leg.stepT += dt / (leg.dodge ? MECHA_DODGE_TIME : MECHA_STEP_TIME);
        if (leg.stepT >= 1) {
          leg.stepT = 1; leg.stepping = false;
          const wasDodge = leg.dodge; leg.dodge = false; leg.planted.copy(leg.stepTo);
          if (!wasDodge) sfx.play("mechastep", leg.planted); // ground stomp at the foot (dodge-yanks aren't steps)
        }
        else { leg.planted.lerpVectors(leg.stepFrom, leg.stepTo, leg.stepT); leg.planted.y += Math.sin(leg.stepT * Math.PI) * (leg.dodge ? MECHA_DODGE_LIFT : MECHA_STEP_HEIGHT); }
      } else {
        this.restFootWorld(leg, _mrest, arena);
        const d2 = (leg.planted.x - _mrest.x) ** 2 + (leg.planted.z - _mrest.z) ** 2;
        // diagonal gait: a leg lifts while its opposite pair is grounded — UNLESS it has strayed so far
        // (MECHA_STEP_FORCE) that waiting would drag it; then it steps now so no foot is ever starved.
        const otherPair = (i === 0 || i === 3) ? [1, 2] : [0, 3];
        const ready = otherPair.every((j) => !legs[j].stepping);
        if ((d2 > MECHA_STEP_DIST * MECHA_STEP_DIST && ready) || d2 > MECHA_STEP_FORCE * MECHA_STEP_FORCE) {
          leg.stepping = true; leg.stepT = 0; leg.stepFrom.copy(leg.planted);
          const tx = _mrest.x + ldx * lead, tz = _mrest.z + ldz * lead; // land AHEAD in the move direction
          leg.stepTo.set(tx, this.floorY + (arena ? arena.surfaceTop(tx, tz) : 0), tz);
        } else {
          leg.planted.y = this.floorY + (arena ? arena.surfaceTop(leg.planted.x, leg.planted.z) : 0); // keep on the surface
        }
      }
      const w = this.legWorld[i];
      w.x = leg.planted.x; w.z = leg.planted.z; w.alive = this.legAlive[i];
    }
  }

  /** MECHA: yank leg `i`'s foot to a new world spot (`tx`,`tz`) to dodge an incoming shot. */
  dodgeLeg(i: number, tx: number, tz: number): void {
    if (!this.mecha) return;
    const leg = this.mecha.legs[i];
    if (!this.legAlive[i] || leg.stepping) return; // already mid-step
    leg.stepping = true; leg.dodge = true; leg.stepT = 0;
    leg.stepFrom.copy(leg.planted);
    leg.stepTo.set(tx, this.floorY, tz);
  }

  /** MECHA: destroy leg `i` (it crumples). Sets limp at 1 lost, toppling at legsToTopple. */
  loseLeg(i: number): void {
    if (!this.legAlive[i]) return;
    this.legAlive[i] = false;
    this.legWorld[i].alive = false;
    if (this.legsLost >= MECHA.legsToTopple && !this.toppling) {
      this.toppling = true;
      sfx.play("mechafall", this.pos); // metallic groan as the walker topples (once, on the false->true edge)
    }
  }

  /** MECHA: solve the knee position for a 2-bone leg reaching `foot` from `hip` (bodyGroup-local),
   *  with the knee bulging outward+up (the pole). Writes the knee into `out`. */
  private solveKnee(hip: THREE.Vector3, foot: THREE.Vector3, l1: number, l2: number, out: THREE.Vector3): void {
    _mn.subVectors(foot, hip);
    let d = _mn.length() || 1e-3;
    _mn.multiplyScalar(1 / d);                              // unit hip→foot
    d = Math.max(Math.abs(l1 - l2) + 0.5, Math.min(l1 + l2 - 0.5, d)); // keep solvable
    const a = (l1 * l1 - l2 * l2 + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(0, l1 * l1 - a * a));
    _mpole.set(hip.x, 0, hip.z); if (_mpole.lengthSq() < 1e-4) _mpole.set(0, 0, 1);
    _mpole.normalize(); _mpole.y = 0.5; _mpole.normalize();  // knees bulge outward + slightly up
    _mb.copy(_mpole).addScaledVector(_mn, -_mpole.dot(_mn)); // pole perpendicular to the leg axis
    if (_mb.lengthSq() < 1e-4) _mb.set(0, 1, 0); else _mb.normalize();
    out.copy(hip).addScaledVector(_mn, a).addScaledVector(_mb, h);
  }

  /** MECHA: pose the walker — body crouch height + topple tilt, then 2-bone IK each leg so the
   *  bones span hip→knee→(planted foot). Feet stay planted; crouching folds the knees. */
  private syncMecha(): void {
    const m = this.mecha!;
    m.root.rotation.z = this.toppling ? -this.toppleT * 1.4 : 0; // crash onto its side while toppling
    // lower to crouch (ground target) OR raise up toward a road (elevated target); topple overrides
    const lift = this.toppling ? -this.toppleT * MECHA_CROUCH_DROP : (this.mechaRaise * this.mechaRaiseY - this.mechaCrouch * MECHA_CROUCH_DROP);
    m.bodyGroup.position.y = lift;
    const c = Math.cos(this.bodyYaw), s = Math.sin(this.bodyYaw);
    const reach = m.l1 + m.l2;
    for (let i = 0; i < m.legs.length; i++) {
      const leg = m.legs[i];
      if (!this.legAlive[i]) { leg.upper.visible = leg.lower.visible = false; continue; } // destroyed leg vanishes
      // planted foot WORLD → bodyGroup-local (un-translate pos, un-rotate bodyYaw, un-lift)
      const rx = leg.planted.x - this.pos.x, rz = leg.planted.z - this.pos.z;
      _mfoot.set(c * rx - s * rz, (leg.planted.y - this.floorY) - lift, s * rx + c * rz);
      const d = _mv.subVectors(_mfoot, leg.hipLocal).length() || 1e-3;
      let k = 1; // bone stretch factor (1 = natural; >1 = leg physically extends to reach a far foot)
      if (d > reach) {
        // OVER-REACH: rather than leave the foot floating short, STRAIGHTEN + STRETCH both bones to bridge
        // the gap (the foot stays glued to the ground; the leg extends like a piston).
        k = Math.min(d / reach, 2.5);
        _mv.multiplyScalar(1 / d);                         // unit hip→foot
        _mknee.copy(leg.hipLocal).addScaledVector(_mv, m.l1 * k); // knee proportional along the straight line
      } else {
        this.solveKnee(leg.hipLocal, _mfoot, m.l1, m.l2, _mknee); // normal bent IK (knee bulges via the pole)
      }
      // live bend-plane normal (shared by BOTH bones) = cross(knee-hip, foot-knee). Degenerate when the
      // leg is straight (over-reach) → track the REST normal swung by the leg so roll never pops.
      _pSide.subVectors(_mknee, leg.hipLocal);
      _pNorm.subVectors(_mfoot, _mknee);
      _poseNormal.crossVectors(_pSide, _pNorm);
      if (_poseNormal.lengthSq() < 1e-6) {
        _bq.setFromUnitVectors(m.upAxis, _pSide.normalize());
        _poseNormal.copy(m.restNormal).applyQuaternion(_bq).normalize();
      } else _poseNormal.normalize();
      this.poseBone(leg.upper, leg.hipLocal, m.upAxis, m.restNormal, _mdir.subVectors(_mknee, leg.hipLocal).normalize(), _poseNormal, k);
      this.poseBone(leg.lower, _mknee, m.loAxis, m.restNormal, _mdir.subVectors(_mfoot, _mknee).normalize(), _poseNormal, k);
    }
  }

  /** Pose one leg bone: orient it with a TRIAD rotation that aligns BOTH the local length `axis`→`dir`
   *  AND the rest bend-plane normal→the live `poseNormal`, so the asymmetric mesh keeps its designed
   *  ROLL (no knee spikes poking out). R = M_pose · M_restᵀ. When k>1, stretch length along the local
   *  axis (R · S). Degenerate triads fall back to the old roll-free setFromUnitVectors. */
  private poseBone(bone: THREE.Object3D, origin: THREE.Vector3, axis: THREE.Vector3, restNormal: THREE.Vector3, dir: THREE.Vector3, poseNormal: THREE.Vector3, k: number): void {
    _tSide.crossVectors(restNormal, axis);   // rest in-plane tangent (⟂ length & normal)
    _pSide.crossVectors(poseNormal, dir);    // pose in-plane tangent
    if (_tSide.lengthSq() < 1e-8 || _pSide.lengthSq() < 1e-8) {
      _bq.setFromUnitVectors(axis, dir); _bR.makeRotationFromQuaternion(_bq); // degenerate → roll-free
    } else {
      _tSide.normalize(); _pSide.normalize();
      _tNorm.copy(restNormal).normalize(); _pNorm.copy(poseNormal).normalize();
      _tAxis.copy(axis);
      _triRest.makeBasis(_tAxis, _tSide, _tNorm); _triRest.transpose(); // orthonormal ⇒ inverse = transpose
      _triPose.makeBasis(dir, _pSide, _pNorm);
      _bR.multiplyMatrices(_triPose, _triRest); // R = M_pose · M_restᵀ (maps rest triad → pose triad)
    }
    if (k !== 1) {
      const e = k - 1, ax = axis.x, ay = axis.y, az = axis.z; // S = I + (k-1)·axis⊗axis (scale along the local bone axis)
      _bS.set(
        1 + e * ax * ax, e * ax * ay, e * ax * az, 0,
        e * ay * ax, 1 + e * ay * ay, e * ay * az, 0,
        e * az * ax, e * az * ay, 1 + e * az * az, 0,
        0, 0, 0, 1,
      );
      _bM.multiplyMatrices(_bR, _bS);
    } else {
      _bM.copy(_bR);
    }
    _bM.setPosition(origin.x, origin.y, origin.z);
    bone.matrixAutoUpdate = false; // we drive .matrix directly (non-uniform along-axis scale)
    bone.matrix.copy(_bM);
    bone.matrixWorldNeedsUpdate = true;
  }

  /** Push state to the meshes. */
  sync(): void {
    if (this.riseMarker && this.riseTime <= 0) this.clearRiseMarker(); // fully surfaced — drop the telegraph
    this.root.position.set(this.pos.x, this.floorY, this.pos.z);
    this.root.rotation.y = this.bodyYaw;
    this.parts.turretPivot.rotation.y = this.turretYaw - this.bodyYaw;
    if (this.isMecha) this.syncMecha();
    if (this.label) this.label.position.set(this.pos.x, this.floorY + (this.isMecha ? 56 : 20), this.pos.z);
    if (this.shieldSprite) this.shieldSprite.position.set(this.pos.x, this.floorY + (this.isMecha ? 64 : 28), this.pos.z);
  }

  dispose(): void {
    this.parts.dispose();
    this.labelTex?.dispose();
    this.shieldTex?.dispose();
  }
}
